/* ════════════════════════════════════════════════════════════════
   FUTURISTIC piXcan WORDMARK CONCEPTS
   Each component renders the full "piXcan" wordmark with a unique
   futuristic treatment of the X. Drop-in for any size.
   ════════════════════════════════════════════════════════════════ */

import React from "react";

/* shared lowercase letter wrap */
const Lc = ({ children, className = "" }) => (
  <span
    className={className}
    style={{
      fontFamily: "Unbounded, sans-serif",
      fontWeight: 500,
      letterSpacing: "-0.02em",
      color: "#ffffff",
    }}
  >
    {children}
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ① HOLOGRAPHIC GLITCH X — RGB chromatic aberration
   ───────────────────────────────────────────────────────────────── */
export const W1_Holographic = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        position: "relative",
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg
        viewBox="0 0 100 100"
        width="1em"
        height="1em"
        style={{ position: "absolute", inset: 0 }}
      >
        {/* magenta channel offset left */}
        <g transform="translate(-2, 0)">
          <path
            d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
            fill="#ff2bd5"
            opacity="0.85"
          />
        </g>
        {/* cyan channel offset right */}
        <g transform="translate(2, 0)">
          <path
            d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
            fill="#00f0ff"
            opacity="0.85"
            style={{ mixBlendMode: "screen" }}
          />
        </g>
        {/* white core */}
        <path
          d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
          fill="#ffffff"
          opacity="0.95"
          style={{ mixBlendMode: "screen" }}
        />
        {/* glitch scan lines */}
        <rect
          x="0"
          y="38"
          width="100"
          height="3"
          fill="#5ef0ff"
          opacity="0.55"
        />
        <rect
          x="0"
          y="62"
          width="100"
          height="2"
          fill="#ff6ee7"
          opacity="0.45"
        />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ② WIREFRAME X — CAD-style 3D wireframe with depth edges
   ───────────────────────────────────────────────────────────────── */
export const W2_Wireframe = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <defs>
          <linearGradient id="w2-edge" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5ef0ff" />
            <stop offset="100%" stopColor="#a88bff" />
          </linearGradient>
        </defs>
        {/* back face */}
        <g
          stroke="#a88bff"
          strokeWidth="1.6"
          fill="none"
          opacity="0.4"
          transform="translate(8, -8)"
        >
          <path d="M16 8 L30 8 L84 92 L70 92 Z" />
          <path d="M84 8 L70 8 L16 92 L30 92 Z" />
        </g>
        {/* depth lines */}
        <g stroke="#5ef0ff" strokeWidth="0.8" opacity="0.55">
          <line x1="16" y1="8" x2="24" y2="0" />
          <line x1="30" y1="8" x2="38" y2="0" />
          <line x1="84" y1="92" x2="92" y2="84" />
          <line x1="70" y1="92" x2="78" y2="84" />
          <line x1="84" y1="8" x2="92" y2="0" />
          <line x1="70" y1="8" x2="78" y2="0" />
          <line x1="16" y1="92" x2="24" y2="84" />
          <line x1="30" y1="92" x2="38" y2="84" />
        </g>
        {/* front face */}
        <g
          stroke="url(#w2-edge)"
          strokeWidth="2.2"
          fill="none"
          strokeLinejoin="round"
        >
          <path d="M16 8 L30 8 L84 92 L70 92 Z" />
          <path d="M84 8 L70 8 L16 92 L30 92 Z" />
        </g>
        {/* center node */}
        <circle cx="50" cy="50" r="3" fill="#5ef0ff" />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ③ PHOTON BURST X — X with radiating spectrum beams
   ───────────────────────────────────────────────────────────────── */
export const W3_PhotonBurst = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        position: "relative",
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <defs>
          <radialGradient id="w3-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#5ef0ff" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#5ef0ff" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="w3-x1" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff6ee7" />
            <stop offset="100%" stopColor="#5ef0ff" />
          </linearGradient>
          <linearGradient id="w3-x2" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#a6ff4d" />
            <stop offset="100%" stopColor="#ffb347" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="50" fill="url(#w3-glow)" />
        {/* burst rays */}
        {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a) => {
          const rad = (a * Math.PI) / 180;
          const x = 50 + Math.cos(rad) * 48;
          const y = 50 + Math.sin(rad) * 48;
          const x0 = 50 + Math.cos(rad) * 28;
          const y0 = 50 + Math.sin(rad) * 28;
          return (
            <line
              key={a}
              x1={x0}
              y1={y0}
              x2={x}
              y2={y}
              stroke="#5ef0ff"
              strokeWidth="0.7"
              opacity="0.6"
            />
          );
        })}
        {/* X core */}
        <path d="M16 8 L30 8 L84 92 L70 92 Z" fill="url(#w3-x1)" />
        <path d="M84 8 L70 8 L16 92 L30 92 Z" fill="url(#w3-x2)" />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ④ HEX LATTICE X — X carved from a silicon hex grid
   ───────────────────────────────────────────────────────────────── */
export const W4_HexLattice = ({ size = 64 }) => {
  // hex grid pattern, fill a hex with spectrum if it lies along the X diagonals
  const hexes = [];
  const rows = 9;
  const cols = 9;
  const dx = 11.5,
    dy = 10;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const xc = 6 + c * dx + (r % 2 === 1 ? dx / 2 : 0);
      const yc = 6 + r * dy;
      // diagonal proximity
      const onD1 = Math.abs(xc - yc) < 9;
      const onD2 = Math.abs(xc + yc - 100) < 9;
      const onX = onD1 || onD2;
      hexes.push({ xc, yc, onX, onBoth: onD1 && onD2 });
    }
  }
  const HEX = "M0,-5 L4.3,-2.5 L4.3,2.5 L0,5 L-4.3,2.5 L-4.3,-2.5 Z";
  return (
    <span
      className="inline-flex items-center"
      style={{ fontSize: size, lineHeight: 1 }}
    >
      <Lc>pi</Lc>
      <span
        style={{
          display: "inline-block",
          width: "1em",
          height: "1em",
          margin: "0 0.04em",
        }}
      >
        <svg viewBox="0 0 100 100" width="1em" height="1em">
          <defs>
            <linearGradient id="w4-on" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ff6ee7" />
              <stop offset="50%" stopColor="#a88bff" />
              <stop offset="100%" stopColor="#5ef0ff" />
            </linearGradient>
          </defs>
          {hexes.map((h, i) => (
            <path
              key={i}
              d={HEX}
              transform={`translate(${h.xc}, ${h.yc})`}
              fill={h.onX ? "url(#w4-on)" : "transparent"}
              stroke={h.onX ? "transparent" : "#ffffff"}
              strokeOpacity={h.onX ? 0 : 0.18}
              strokeWidth="0.6"
              opacity={h.onBoth ? 1 : h.onX ? 0.95 : 1}
            />
          ))}
        </svg>
      </span>
      <Lc>can</Lc>
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ⑤ QUANTUM LAYERED X — three rotated X's superposed
   ───────────────────────────────────────────────────────────────── */
export const W5_Quantum = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        {/* layer 1 — magenta, rotated -8 */}
        <g transform="rotate(-8 50 50)">
          <path
            d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
            fill="#ff6ee7"
            opacity="0.55"
            style={{ mixBlendMode: "screen" }}
          />
        </g>
        {/* layer 2 — violet, no rotation */}
        <g>
          <path
            d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
            fill="#a88bff"
            opacity="0.6"
            style={{ mixBlendMode: "screen" }}
          />
        </g>
        {/* layer 3 — cyan, rotated +8 */}
        <g transform="rotate(8 50 50)">
          <path
            d="M16 8 L30 8 L84 92 L70 92 Z M84 8 L70 8 L16 92 L30 92 Z"
            fill="#5ef0ff"
            opacity="0.6"
            style={{ mixBlendMode: "screen" }}
          />
        </g>
        {/* center bright dot */}
        <circle cx="50" cy="50" r="2.5" fill="#fff" />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ⑥ OPTICAL FIBER X — X built from glowing fiber strands
   ───────────────────────────────────────────────────────────────── */
export const W6_Fiber = ({ size = 64 }) => {
  const strands = 7;
  const strandColors = [
    "#ff6ee7",
    "#c97bff",
    "#a88bff",
    "#7d9bff",
    "#5ef0ff",
    "#a6ff4d",
    "#ffb347",
  ];
  const strandsArr = Array.from({ length: strands });
  return (
    <span
      className="inline-flex items-center"
      style={{ fontSize: size, lineHeight: 1 }}
    >
      <Lc>pi</Lc>
      <span
        style={{
          display: "inline-block",
          width: "1em",
          height: "1em",
          margin: "0 0.04em",
        }}
      >
        <svg viewBox="0 0 100 100" width="1em" height="1em">
          <defs>
            <filter id="w6-glow">
              <feGaussianBlur stdDeviation="1.2" />
            </filter>
          </defs>
          {/* diagonal 1: top-left → bottom-right */}
          {strandsArr.map((_, i) => {
            const off = (i - (strands - 1) / 2) * 3.5;
            return (
              <line
                key={`d1-${i}`}
                x1={20 + off}
                y1={10}
                x2={80 + off}
                y2={90}
                stroke={strandColors[i]}
                strokeWidth="1.4"
                strokeLinecap="round"
                opacity="0.85"
              />
            );
          })}
          {/* diagonal 2: top-right → bottom-left */}
          {strandsArr.map((_, i) => {
            const off = (i - (strands - 1) / 2) * 3.5;
            return (
              <line
                key={`d2-${i}`}
                x1={80 - off}
                y1={10}
                x2={20 - off}
                y2={90}
                stroke={strandColors[i]}
                strokeWidth="1.4"
                strokeLinecap="round"
                opacity="0.85"
              />
            );
          })}
          {/* glowing fiber tips */}
          {strandsArr.map((_, i) => {
            const off = (i - (strands - 1) / 2) * 3.5;
            return (
              <g key={`tips-${i}`}>
                <circle
                  cx={20 + off}
                  cy={10}
                  r="1.6"
                  fill={strandColors[i]}
                  filter="url(#w6-glow)"
                />
                <circle
                  cx={80 + off}
                  cy={90}
                  r="1.6"
                  fill={strandColors[i]}
                  filter="url(#w6-glow)"
                />
                <circle
                  cx={80 - off}
                  cy={10}
                  r="1.6"
                  fill={strandColors[i]}
                  filter="url(#w6-glow)"
                />
                <circle
                  cx={20 - off}
                  cy={90}
                  r="1.6"
                  fill={strandColors[i]}
                  filter="url(#w6-glow)"
                />
              </g>
            );
          })}
        </svg>
      </span>
      <Lc>can</Lc>
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ⑦ MINIMALIST MONO — pure white refined X with a single accent dot
   ───────────────────────────────────────────────────────────────── */
export const W7_Minimal = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <g
          stroke="#ffffff"
          strokeWidth="6"
          strokeLinecap="square"
          fill="none"
        >
          <line x1="20" y1="14" x2="80" y2="86" />
          <line x1="80" y1="14" x2="20" y2="86" />
        </g>
        {/* one cyan accent dot at the crossing */}
        <circle cx="50" cy="50" r="3" fill="#5ef0ff" />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ⑧ BRUTALIST BLOCK — heavy slab X with hard-edged grit
   ───────────────────────────────────────────────────────────────── */
export const W8_Brutalist = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc className="text-white" >pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <defs>
          <pattern
            id="w8-grit"
            x="0"
            y="0"
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
          >
            <rect width="6" height="6" fill="#ffffff" />
            <rect x="0" y="3" width="6" height="0.7" fill="#0a0a18" opacity="0.55" />
          </pattern>
        </defs>
        {/* heavy slabs */}
        <path d="M8 6 L26 6 L92 94 L74 94 Z" fill="url(#w8-grit)" />
        <path d="M92 6 L74 6 L8 94 L26 94 Z" fill="url(#w8-grit)" />
        {/* spectrum slash through center */}
        <rect
          x="0"
          y="48"
          width="100"
          height="4"
          fill="#5ef0ff"
          opacity="0.85"
        />
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ⑨ NEON TUBE — outlined glowing X like a neon sign
   ───────────────────────────────────────────────────────────────── */
export const W9_Neon = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
        filter:
          "drop-shadow(0 0 4px #ff6ee7) drop-shadow(0 0 10px #ff6ee7) drop-shadow(0 0 18px rgba(255,110,231,0.6))",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <defs>
          <linearGradient id="w9-tube" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff6ee7" />
            <stop offset="100%" stopColor="#ff8de9" />
          </linearGradient>
        </defs>
        {/* tube outline */}
        <g
          stroke="url(#w9-tube)"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
        >
          <line x1="22" y1="14" x2="78" y2="86" />
          <line x1="78" y1="14" x2="22" y2="86" />
        </g>
        {/* inner bright core */}
        <g
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
          opacity="0.95"
        >
          <line x1="22" y1="14" x2="78" y2="86" />
          <line x1="78" y1="14" x2="22" y2="86" />
        </g>
        {/* tube end caps */}
        {[
          [22, 14],
          [78, 14],
          [22, 86],
          [78, 86],
        ].map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="3" fill="#ff6ee7" />
        ))}
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ⑩ ASCII GLYPH — X composed of code characters
   ───────────────────────────────────────────────────────────────── */
export const W10_Ascii = ({ size = 64 }) => {
  const chars = ["0", "1", "/", "\\", "x", "*", ".", "+"];
  // build X pattern: positions on 9x9 grid where r==c or r+c==8
  const cells = [];
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const onX = r === c || r + c === 8;
      const near = Math.abs(r - c) === 1 || Math.abs(r + c - 8) === 1;
      if (onX || near) {
        cells.push({
          r,
          c,
          onX,
          ch: chars[(r + c) % chars.length],
        });
      }
    }
  }
  return (
    <span
      className="inline-flex items-center"
      style={{ fontSize: size, lineHeight: 1 }}
    >
      <Lc>pi</Lc>
      <span
        style={{
          display: "inline-block",
          width: "1em",
          height: "1em",
          margin: "0 0.04em",
        }}
      >
        <svg viewBox="0 0 100 100" width="1em" height="1em">
          {cells.map((c, i) => (
            <text
              key={i}
              x={10 + c.c * 10}
              y={16 + c.r * 10}
              fontFamily="JetBrains Mono, monospace"
              fontSize="9"
              fontWeight="700"
              fill={c.onX ? "#5ef0ff" : "#ffffff"}
              opacity={c.onX ? 1 : 0.25}
              textAnchor="middle"
            >
              {c.ch}
            </text>
          ))}
        </svg>
      </span>
      <Lc>can</Lc>
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ⑪ LIQUID CAUSTIC — soft caustic light pattern shaping the X
   ───────────────────────────────────────────────────────────────── */
export const W11_Caustic = ({ size = 64 }) => (
  <span
    className="inline-flex items-center"
    style={{ fontSize: size, lineHeight: 1 }}
  >
    <Lc>pi</Lc>
    <span
      style={{
        display: "inline-block",
        width: "1em",
        height: "1em",
        margin: "0 0.04em",
      }}
    >
      <svg viewBox="0 0 100 100" width="1em" height="1em">
        <defs>
          <linearGradient id="w11-a" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5ef0ff" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="100%" stopColor="#a88bff" stopOpacity="0.9" />
          </linearGradient>
          <linearGradient id="w11-b" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff6ee7" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#ffffff" stopOpacity="1" />
            <stop offset="100%" stopColor="#a6ff4d" stopOpacity="0.9" />
          </linearGradient>
          <filter id="w11-blur">
            <feGaussianBlur stdDeviation="1.6" />
          </filter>
        </defs>
        {/* soft caustic glow */}
        <g filter="url(#w11-blur)" opacity="0.55">
          <path
            d="M14 6 Q 24 8 28 12 L 86 88 Q 92 92 88 94 L 70 94 Q 64 90 60 84 L 14 18 Q 10 12 14 6 Z"
            fill="url(#w11-a)"
          />
          <path
            d="M86 6 Q 76 8 72 12 L 14 88 Q 8 92 12 94 L 30 94 Q 36 90 40 84 L 86 18 Q 90 12 86 6 Z"
            fill="url(#w11-b)"
          />
        </g>
        {/* sharp caustic highlights */}
        <g
          stroke="#ffffff"
          strokeWidth="1.4"
          strokeLinecap="round"
          fill="none"
          opacity="0.9"
        >
          <path d="M18 10 Q 26 16 32 24 L 80 84 Q 86 90 82 92" />
          <path d="M82 10 Q 74 16 68 24 L 18 84 Q 14 90 18 92" />
        </g>
      </svg>
    </span>
    <Lc>can</Lc>
  </span>
);

/* ─────────────────────────────────────────────────────────────────
   ⑫ DATA STREAM — X with binary 0/1 streaming inside
   ───────────────────────────────────────────────────────────────── */
export const W12_DataStream = ({ size = 64 }) => {
  // generate stream characters along both diagonals
  const bits1 = [];
  const bits2 = [];
  for (let t = 0; t <= 18; t++) {
    const x1 = 14 + (72 * t) / 18;
    const y1 = 14 + (72 * t) / 18;
    const x2 = 86 - (72 * t) / 18;
    const y2 = 14 + (72 * t) / 18;
    bits1.push({ x: x1, y: y1, b: (t * 7) % 2 });
    bits2.push({ x: x2, y: y2, b: (t * 13) % 2 });
  }
  return (
    <span
      className="inline-flex items-center"
      style={{ fontSize: size, lineHeight: 1 }}
    >
      <Lc>pi</Lc>
      <span
        style={{
          display: "inline-block",
          width: "1em",
          height: "1em",
          margin: "0 0.04em",
        }}
      >
        <svg viewBox="0 0 100 100" width="1em" height="1em">
          <defs>
            <linearGradient id="w12-line1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ff6ee7" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#5ef0ff" stopOpacity="0.6" />
            </linearGradient>
            <linearGradient id="w12-line2" x1="1" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#a6ff4d" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#ffb347" stopOpacity="0.6" />
            </linearGradient>
          </defs>
          {/* faint guide diagonals */}
          <line
            x1="14"
            y1="14"
            x2="86"
            y2="86"
            stroke="url(#w12-line1)"
            strokeWidth="1"
          />
          <line
            x1="86"
            y1="14"
            x2="14"
            y2="86"
            stroke="url(#w12-line2)"
            strokeWidth="1"
          />
          {bits1.map((b, i) => (
            <text
              key={`a-${i}`}
              x={b.x}
              y={b.y + 2}
              fontFamily="JetBrains Mono, monospace"
              fontSize="6.5"
              fontWeight="700"
              fill="#5ef0ff"
              textAnchor="middle"
              opacity={0.3 + (i % 5) * 0.14}
            >
              {b.b}
            </text>
          ))}
          {bits2.map((b, i) => (
            <text
              key={`b-${i}`}
              x={b.x}
              y={b.y + 2}
              fontFamily="JetBrains Mono, monospace"
              fontSize="6.5"
              fontWeight="700"
              fill="#ff6ee7"
              textAnchor="middle"
              opacity={0.3 + (i % 5) * 0.14}
            >
              {b.b}
            </text>
          ))}
          {/* bright photon at center crossing */}
          <circle cx="50" cy="50" r="3" fill="#ffffff" />
        </svg>
      </span>
      <Lc>can</Lc>
    </span>
  );
};

/* Catalog metadata */
export const futuristicConcepts = [
  {
    id: 7,
    name: "Holographic Glitch",
    file: "07_holographic-glitch.svg",
    Wm: W1_Holographic,
    description:
      "RGB chromatic aberration with horizontal scan lines. Pure cyberpunk — the X feels like a hologram glitching into existence.",
    keywords: ["cyberpunk", "rgb-split", "scan-lines"],
  },
  {
    id: 8,
    name: "Wireframe CAD",
    file: "08_wireframe-cad.svg",
    Wm: W2_Wireframe,
    description:
      "3D wireframe with depth edges and a center node. Reads as engineering blueprint — precise, technical, manufacturable.",
    keywords: ["3d", "blueprint", "engineering"],
  },
  {
    id: 9,
    name: "Photon Burst",
    file: "09_photon-burst.svg",
    Wm: W3_PhotonBurst,
    description:
      "Twelve spectrum rays radiating from a luminous X core. Pure photonic energy — every retrieval is a burst of light.",
    keywords: ["radiant", "explosive", "luminous"],
  },
  {
    id: 10,
    name: "Hex Lattice",
    file: "10_hex-lattice.svg",
    Wm: W4_HexLattice,
    description:
      "X carved from a silicon hexagonal grid — the wafer aesthetic of your original logo, evolved into pure geometry.",
    keywords: ["silicon", "wafer", "structural"],
  },
  {
    id: 11,
    name: "Quantum Superposition",
    file: "11_quantum-superposition.svg",
    Wm: W5_Quantum,
    description:
      "Three translucent X's at different rotations, blended additively. Suggests parallel computation, multiple states at once.",
    keywords: ["quantum", "layered", "ethereal"],
  },
  {
    id: 12,
    name: "Optical Fiber Bundle",
    file: "12_optical-fiber-bundle.svg",
    Wm: W6_Fiber,
    description:
      "Two crossed bundles of glowing fiber strands. Most literal photonic-tech read — light traveling through silicon.",
    keywords: ["fiber-optic", "literal", "photonic"],
  },
  {
    id: 13,
    name: "Minimalist Mono",
    file: "13_minimalist-mono.svg",
    Wm: W7_Minimal,
    description:
      "Pure white, refined, single-weight X with one cyan dot at the crossing. Maximum restraint — lets the brand name carry the weight.",
    keywords: ["minimal", "refined", "swiss"],
  },
  {
    id: 14,
    name: "Brutalist Block",
    file: "14_brutalist-block.svg",
    Wm: W8_Brutalist,
    description:
      "Heavy slab X with hard-edged grit texture and a single cyan slash through the middle. Industrial, unapologetic, raw.",
    keywords: ["brutalist", "industrial", "bold"],
  },
  {
    id: 15,
    name: "Neon Tube",
    file: "15_neon-tube.svg",
    Wm: W9_Neon,
    description:
      "Magenta neon-sign tube X with a luminous halo and end caps. Vintage cyberpunk meets photonic future.",
    keywords: ["neon", "retro-future", "glow"],
  },
  {
    id: 16,
    name: "ASCII Glyph",
    file: "16_ascii-glyph.svg",
    Wm: W10_Ascii,
    description:
      "X composed of monospace characters (0, 1, /, \\, x, *). For developer-first audiences — feels like a terminal banner.",
    keywords: ["terminal", "developer", "monospace"],
  },
  {
    id: 17,
    name: "Liquid Caustic",
    file: "17_liquid-caustic.svg",
    Wm: W11_Caustic,
    description:
      "Soft caustic light patterns shaping the X — like sunlight refracted through water. Organic, warm, photonically beautiful.",
    keywords: ["caustic", "organic", "soft"],
  },
  {
    id: 18,
    name: "Data Stream",
    file: "18_data-stream.svg",
    Wm: W12_DataStream,
    description:
      "Binary 0/1 stream flowing along both diagonals into a bright photon at center. Tells the retrieval story with code.",
    keywords: ["binary", "narrative", "developer"],
  },
];
